/**
 * PATH: packages/email-hub/src/feature/email-layout/EmailSidebarNavigation.tsx
 * FILE_ID: EMAIL-LAYOUT-SIDEBAR-NAV-001
 * PURPOSE: Render canonical Email folder navigation and badges.
 */

import { NavLink } from 'react-router-dom'
import { Forward, Layers3 } from 'lucide-react'
import { formatCountBadge } from '@quotech/ui'
import { cn } from '../../_internal/lib/utils'
import { EMAIL_NAV_ITEMS, type EmailNavItem } from '../config/navigation'
import { emailPath } from '../lib/basePath'
import { usePersonalMembershipCount } from '../personal/usePersonalData'
import { useReferralSummary } from '../referrals'
import { AddMailboxNavRow } from './AddMailboxNavRow'
import type { EmailLayoutController } from './useEmailLayoutController'

const ALL_INBOXES_NAV_ITEM: EmailNavItem = {
  id: 'all-inboxes',
  label: 'All inboxes',
  icon: Layers3,
  href: '/email/personal',
}

const REFERRED_NAV_ITEM: EmailNavItem = {
  id: 'referred',
  label: 'Referred',
  icon: Forward,
  href: '/email/referred',
}

interface NavigationBadge {
  count: number
  tone: 'primary' | 'neutral'
}

function resolveNavigationBadge(
  itemId: string,
  layout: EmailLayoutController,
): NavigationBadge {
  if (itemId === 'inbox') {
    return { count: layout.stats?.unread ?? 0, tone: 'primary' }
  }
  if (itemId === 'drafts') {
    return { count: layout.draftCount, tone: 'neutral' }
  }
  if (itemId === 'starred') {
    return { count: layout.stats?.starred ?? 0, tone: 'neutral' }
  }
  return { count: 0, tone: 'primary' }
}

function EmailNavigationItem({
  item,
  layout,
  countOverride,
}: {
  item: EmailNavItem
  layout: EmailLayoutController
  countOverride?: number
}) {
  const Icon = item.icon
  const badge = countOverride == null
    ? resolveNavigationBadge(item.id, layout)
    : { count: countOverride, tone: 'primary' as const }
  const expandedBadge =
    badge.tone === 'neutral'
      ? 'text-[11px] bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 min-w-[20px] text-center font-medium'
      : 'text-[11px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 min-w-[20px] text-center font-medium'
  const collapsedBadge =
    badge.tone === 'neutral'
      ? 'absolute -top-0.5 -right-0.5 text-[9px] bg-muted text-foreground rounded-full min-w-4 h-4 px-1 flex items-center justify-center font-bold leading-none'
      : 'absolute -top-0.5 -right-0.5 text-[9px] bg-primary text-primary-foreground rounded-full min-w-4 h-4 px-1 flex items-center justify-center font-bold leading-none'

  return (
    <NavLink
      to={emailPath(item.href.replace(/^\/email/, ''))}
      title={layout.collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'w-full flex items-center rounded-lg transition-colors',
          layout.collapsed
            ? 'justify-center h-9 w-9 mx-auto relative'
            : 'justify-between px-3 py-2 text-sm',
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      {layout.collapsed ? (
        <>
          <Icon className="w-4 h-4" />
          {badge.count > 0 && (
            <span className={collapsedBadge}>
              {formatCountBadge(badge.count)}
            </span>
          )}
        </>
      ) : (
        <>
          <span className="flex items-center gap-2.5">
            <Icon className="w-4 h-4" />
            {item.label}
          </span>
          {badge.count > 0 && (
            <span className={expandedBadge}>{badge.count}</span>
          )}
        </>
      )}
    </NavLink>
  )
}

export function EmailSidebarNavigation({
  layout,
}: {
  layout: EmailLayoutController
}) {
  const membershipCount = usePersonalMembershipCount()
  const referralSummary = useReferralSummary()
  const showAllInboxes = (membershipCount.data ?? 0) >= 2
  const showReferred =
    showAllInboxes ||
    (referralSummary.data?.total_count ?? 0) > 0 ||
    referralSummary.data?.has_transfer_grant === true

  return (
    <>
      {!layout.collapsed && (
        <div className="px-3 mb-1">
          <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
            Email
          </p>
        </div>
      )}
      <nav
        className={cn(
          'space-y-0.5',
          layout.collapsed ? 'px-1.5' : 'px-2',
        )}
      >
        <EmailNavigationItem item={EMAIL_NAV_ITEMS[0]} layout={layout} />
        {showAllInboxes && (
          <EmailNavigationItem item={ALL_INBOXES_NAV_ITEM} layout={layout} />
        )}
        {showReferred && (
          <EmailNavigationItem
            item={REFERRED_NAV_ITEM}
            layout={layout}
            countOverride={referralSummary.data?.pending_count ?? 0}
          />
        )}
        {EMAIL_NAV_ITEMS.slice(1).map((item) => (
          <EmailNavigationItem key={item.id} item={item} layout={layout} />
        ))}
        <AddMailboxNavRow
          variant={layout.collapsed ? 'rail-collapsed' : 'rail'}
          open={layout.addMailboxPickerOpen}
          setOpen={layout.setAddMailboxPickerOpen}
          onMicrosoft={() => {
            void layout.connectOutlook()
          }}
          onGoogle={() => {
            void layout.gmail.authorize()
          }}
        />
      </nav>
    </>
  )
}
